/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import './license-delete-dialog.component.less';

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { ILicense } from 'ng/modules/licensing/licensing.types';
import moment from 'moment';
import { InitialDataService } from 'ng/modules/core/services/initial-data/initial-data.service';

import * as globalL10n from 'global-l10n';
import * as l10n from './license-delete-dialog.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Dialog popup for deleting licenses from License list page.
 * @author Shubham Bindal
 */
@Component({
    selector: 'license-delete-dialog',
    templateUrl: './license-delete-dialog.component.html',
})
export class LicenseDeleteDialogComponent implements OnInit {
    @Input()
    public licenses: ILicense[];

    @Output()
    public readonly onClose = new EventEmitter<void>();

    @Output()
    public readonly onDelete = new EventEmitter<boolean>();

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Grid config to show the list of licenses for user confirmation.
     */
    public licensesGridConfig: IAviDataGridConfig;

    private controllerTimeDifference = 0;

    constructor(
        private readonly l10nService: L10nService,
        private readonly initialDataService: InitialDataService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.controllerTimeDifference = this.initialDataService?.controllerTimeDifference ?? 0;

        this.licensesGridConfig = {
            fields: [{
                id: 'license_type',
                label: this.l10nService.getMessage(l10nKeys.columnTitleType),
                transform: (row: ILicense): string => row.license_type ?? '',
            }, {
                id: 'license_id',
                label: this.l10nService.getMessage(l10nKeys.columnTitleLicenseId),
                transform: (row: ILicense): string => row.license_id ?? '',
            }, {
                id: 'serviceCores',
                label: this.l10nService.getMessage(l10nKeys.columnTitleServiceUnits),
                transform: (row: ILicense): number => row.service_cores ?? 0,
            }, {
                id: 'valid_until',
                label: this.l10nService.getMessage(l10nKeys.columnTitleExpiry),
                transform: (row: ILicense): string => this.transformValidUntil(row),
            }],
            layout: {
                hideCheckboxes: true,
                hidePagination: true,
                hideRowsTotal: true,
                hideSearch: true,
            },
        };
    }

    /**
     * Transform valid_until date to display format.
     * @returns Formatted date string or expired label
     * @protected
     */
    private transformValidUntil(license: ILicense): string {
        if (!license.valid_until) {
            return '';
        }

        const validUntil = moment(license.valid_until).add(this.controllerTimeDifference, 'seconds');

        if (validUntil.isBefore()) {
            return this.l10nService.getMessage(l10nKeys.expiredLabel);
        }

        return validUntil.format('ll');
    }
}
